import React from 'react'
import { useDocumentTitle } from '@uidotdev/usehooks'
import PageBase from 'components/page-title/PageBase'
import { type TopPlayerDashboardResult, type LeagueLeadersDashboardViewModel } from 'features/league-leaders/models/leagueLeadersModels'
import { Box, Card, Chip, Divider, Grid, List, ListItem, ListItemAvatar, ListItemText, Skeleton, Stack, Typography } from '@mui/material'
import { alpha, styled } from '@mui/material/styles'
import { PlayerImage } from 'components/shared-components/PlayerImage'
import { TeamImage } from 'components/shared-components/TeamImage'
import { useQuery } from '@tanstack/react-query'
import { ApiQueryService, ApiQueryKeys } from 'services/query/apiQueryService'
import { BarChart, EmojiEvents, Shield, SportsHockey } from '@mui/icons-material'
import NoDataMessageComponent from 'components/app-no-data/AppNoData'
import { FcBarChart } from 'react-icons/fc'
import theme from 'theme/AppTheme'

interface PlayerListHomeDashboardTopLeaderListProps {
  title: string
  topPlayerDashboardResult: TopPlayerDashboardResult[] | undefined
  isLoading: boolean
  accentColor: string
}

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontFamily: 'Spantaran',
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.primary
}))

const DashboardCard = styled(Card)<{ accent: string }>(({ theme, accent }) => ({
  height: '100%',
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${alpha(accent, 0.2)}`,
  background: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(accent, 0.08)} 100%)`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.08)}`
}))

const nhlTeamColors: Record<string, string> = {
  ANA: '#F47A38',
  UTA: '#71AFE5',
  ARI: '#8C2633',
  BOS: '#FFB81C',
  BUF: '#003087',
  CGY: '#C8102E',
  CAR: '#CC0000',
  CHI: '#CF0A2C',
  COL: '#6F263D',
  CBJ: '#002654',
  DAL: '#006847',
  DET: '#CE1126',
  EDM: '#FF4C00',
  FLA: '#C8102E',
  LAK: '#111111',
  MIN: '#154734',
  MTL: '#AF1E2D',
  NSH: '#FFB81C',
  NJD: '#CE1126',
  NYI: '#00539B',
  NYR: '#0038A8',
  OTT: '#C52032',
  PHI: '#F74902',
  PIT: '#FFB81C',
  SEA: '#001628',
  SJS: '#006D75',
  STL: '#002F87',
  TBL: '#002868',
  TOR: '#00205B',
  VAN: '#00205B',
  VGK: '#B4975A',
  WSH: '#041E42',
  WPG: '#041E42'
}

const nhlTeamColorAliases: Record<string, string> = {
  UTAHHC: 'UTA',
  UTH: 'UTA',
  UTAH: 'UTA',
  NJ: 'NJD',
  TB: 'TBL',
  SJ: 'SJS',
  LV: 'VGK'
}

const getTeamRowTint = (teamAbbreviation: string | undefined): string | undefined => {
  if (teamAbbreviation == null || teamAbbreviation.trim().length === 0) {
    return undefined
  }

  const normalized = teamAbbreviation.toUpperCase().replace(/[.\s-]/g, '')
  const aliasResolved = nhlTeamColorAliases[normalized] ?? normalized
  const teamColor = nhlTeamColors[aliasResolved]
  if (teamColor == null) {
    return undefined
  }

  return alpha(teamColor, 0.3)
}

const HomeDashboardTopLeaderList: React.FC<PlayerListHomeDashboardTopLeaderListProps> = ({ title, topPlayerDashboardResult, isLoading, accentColor }) => {
  return (
    <DashboardCard accent={accentColor}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${alpha(accentColor, 0.2)}`,
          backgroundColor: alpha(accentColor, 0.08)
        }}
      >
        <Typography variant='subtitle1' fontWeight={700}>{title}</Typography>
      </Box>
      <List sx={{ width: '100%', py: 0 }}>
        {isLoading && (
          <Box p={1.5}>
            {[1, 2, 3].map((item) => (
              <ListItem key={item} sx={{ px: 1 }}>
                <ListItemAvatar>
                  <Skeleton variant='circular' width={44} height={44} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant='text' width='80%' />}
                  secondary={<Skeleton variant='text' width='55%' />}
                />
                <Skeleton variant='rounded' width={56} height={28} />
              </ListItem>
            ))}
          </Box>
        )}
        {!isLoading && topPlayerDashboardResult?.length === 0 && (
          <Box p={2}>
            <NoDataMessageComponent
              message='No data available at this time for this category'
              icon={FcBarChart({ size: 'calc(36px + 1vw)', color: theme.palette.primary.dark })}
            />
          </Box>
        )}
        {!isLoading && topPlayerDashboardResult?.map((player, index) => (
          <React.Fragment key={`${title}-${player.name}-${index}`}>
            <ListItem
              alignItems='center'
              sx={{
                px: 2,
                py: 1.25,
                backgroundColor: getTeamRowTint(player.teamAbbreviation),
                transition: 'background-color 0.2s ease-in-out'
              }}
            >
              <Typography
                variant='subtitle2'
                sx={{
                  minWidth: 24,
                  mr: 1,
                  color: 'text.secondary',
                  fontWeight: 700
                }}
              >
                {index + 1}.
              </Typography>
              <ListItemAvatar sx={{ paddingRight: '8px' }}>
                <PlayerImage alt={player.name} src={player.playerImageUrl} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography component='span' variant='subtitle1' color='text.primary' fontWeight={600}>
                    {player.name}
                  </Typography>
                }
              />
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography
                  component='span'
                  variant='subtitle1'
                  fontWeight={700}
                  sx={{ color: accentColor }}
                >
                  {player.topResultValue}
                </Typography>
                <TeamImage src={player.teamImageUrl} />
              </Stack>
            </ListItem>
            {index < topPlayerDashboardResult.length - 1 && <Divider component='li' sx={{ mx: 2 }} />}
          </React.Fragment>
        ))}
      </List>
    </DashboardCard>
  )
}

const LeagueLeaders: React.FC = React.memo(() => {
  useDocumentTitle('FantaHockey | League Leaders')
  const [homeDashboard, setHomeDashboard] = React.useState<LeagueLeadersDashboardViewModel | undefined>(undefined)
  const { isLoading, data } = useQuery<LeagueLeadersDashboardViewModel>({
    queryKey: [ApiQueryKeys[ApiQueryKeys.GetLeagueLeadersDashboardQuery]],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetLeagueLeadersDashboardQuery]()).data
    }
  })

  React.useEffect(() => {
    if (data != null) {
      setHomeDashboard(data)
    }
  }, [data])

  const leaderboardSections: Array<{
    title: string
    subtitle: string
    icon: JSX.Element
    accentColor: string
    items: Array<{ title: string, data: TopPlayerDashboardResult[] | undefined }>
  }> = [
    {
      title: 'Forwards',
      subtitle: 'Elite offensive production',
      icon: <SportsHockey fontSize='small' />,
      accentColor: theme.palette.primary.main,
      items: [
        { title: 'Top Forwards By Points', data: homeDashboard?.topForwardsByPoints },
        { title: 'Top Forwards By Goals', data: homeDashboard?.topForwardsByGoals },
        { title: 'Top Forwards By Assists', data: homeDashboard?.topForwardsByAssists },
        { title: 'Top Forwards By TOI Per Game', data: homeDashboard?.topForwardByTimeOnIcePerGame },
        { title: 'Top Forwards By Shots', data: homeDashboard?.topForwardsByShots },
        { title: 'Top Forwards By Plus/Minus', data: homeDashboard?.topForwardsByPlusMinus },
        { title: 'Top Forwards By Power Play Points', data: homeDashboard?.topForwardsByPowerPlayPoints }
      ]
    },
    {
      title: 'Defense',
      subtitle: 'Blue-line impact',
      icon: <Shield fontSize='small' />,
      accentColor: theme.palette.success.main,
      items: [
        { title: 'Top Defense By Points', data: homeDashboard?.topDefenseByPoints },
        { title: 'Top Defense By Goals', data: homeDashboard?.topDefenseByGoals },
        { title: 'Top Defense By Assists', data: homeDashboard?.topDefenseByAssists },
        { title: 'Top Defense By TOI Per Game', data: homeDashboard?.topDefenseByTimeOnIcePerGame },
        { title: 'Top Defense By Shots', data: homeDashboard?.topDefenseByShots },
        { title: 'Top Defense By Plus/Minus', data: homeDashboard?.topDefenseByPlusMinus },
        { title: 'Top Defense By Power Play Points', data: homeDashboard?.topDefenseByPowerPlayPoints }
      ]
    },
    {
      title: 'Goalies',
      subtitle: 'Crease control and consistency',
      icon: <EmojiEvents fontSize='small' />,
      accentColor: theme.palette.warning.main,
      items: [
        { title: 'Top Goalies By Wins', data: homeDashboard?.topGoalieByWins },
        { title: 'Top Goalies By Save Percentage', data: homeDashboard?.topGoalieBySavePercentage },
        { title: 'Top Goalies By Saves', data: homeDashboard?.topGoalieBySaves },
        { title: 'Top Goalies By Shutouts', data: homeDashboard?.topGoalieByShutouts }
      ]
    }
  ]

  return (
    <PageBase icon={BarChart} title={'League Leaders'}>
      <Box p={2}>
        <Card
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 40%, ${alpha(theme.palette.info.light, 0.18)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent='space-between'>
            <Box>
              <Typography variant='h6' fontFamily='Spantaran' sx={{ lineHeight: 1 }}>
                League Leaderboard Center
              </Typography>
              <Typography variant='subtitle2' color='text.secondary' sx={{ mt: 0.5 }}>
                Snapshot of top performers across scoring, possession and goaltending
              </Typography>
            </Box>
          </Stack>
        </Card>

        <Stack spacing={3}>
          {leaderboardSections.map((section) => (
            <Box key={section.title}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                <SectionTitle variant='h5'>
                  {section.icon}
                  {section.title}
                </SectionTitle>
                <Chip
                  size='small'
                  label={section.subtitle}
                  sx={{
                    bgcolor: alpha(section.accentColor, 0.12),
                    color: section.accentColor,
                    fontWeight: 600
                  }}
                />
              </Stack>
              <Grid container spacing={2}>
                {section.items.map((item) => (
                  <Grid key={item.title} item xs={12} md={6} lg={4}>
                    <HomeDashboardTopLeaderList
                      title={item.title}
                      topPlayerDashboardResult={item.data}
                      isLoading={isLoading}
                      accentColor={section.accentColor}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Stack>
      </Box>
    </PageBase>
  )
})

LeagueLeaders.displayName = 'LeagueLeaders'
export default LeagueLeaders
